import * as React from "react";

export interface ScrollEdges {
  top: boolean;
  bottom: boolean;
  left?: boolean;
  right?: boolean;
}

const FADE = "32px";

const NO_EDGES: ScrollEdges = {
  top: false,
  bottom: false,
  left: false,
  right: false,
};

const measure = (node: HTMLElement, axis: "x" | "y"): ScrollEdges =>
  axis === "y"
    ? {
        ...NO_EDGES,
        top: node.scrollTop > 1,
        bottom: node.scrollTop + node.clientHeight < node.scrollHeight - 1,
      }
    : {
        ...NO_EDGES,
        left: node.scrollLeft > 1,
        right: node.scrollLeft + node.clientWidth < node.scrollWidth - 1,
      };

const sameEdges = (a: ScrollEdges, b: ScrollEdges) =>
  a.top === b.top &&
  a.bottom === b.bottom &&
  a.left === b.left &&
  a.right === b.right;

export const useScrollEdges = (enabled: boolean, axis: "x" | "y" = "y") => {
  const [node, setNode] = React.useState<HTMLElement | null>(null);
  const [edges, setEdges] = React.useState<ScrollEdges>(NO_EDGES);

  React.useEffect(() => {
    if (!enabled || !node) return;
    const update = () => {
      const next = measure(node, axis);
      setEdges((previous) => (sameEdges(previous, next) ? previous : next));
    };
    update();
    node.addEventListener("scroll", update, { passive: true });
    const observer =
      typeof ResizeObserver === "undefined"
        ? undefined
        : new ResizeObserver(update);
    observer?.observe(node);
    if (node.firstElementChild) observer?.observe(node.firstElementChild);
    return () => {
      node.removeEventListener("scroll", update);
      observer?.disconnect();
    };
  }, [enabled, node, axis]);

  return { setNode, edges };
};

const fade = (direction: string, start?: boolean, end?: boolean) =>
  `linear-gradient(to ${direction}, ${start ? "transparent" : "black"} 0, black ${start ? FADE : "0px"}, black calc(100% - ${end ? FADE : "0px"}), ${end ? "transparent" : "black"} 100%)`;

export const getEdgeFadeStyle = ({
  top,
  bottom,
  left,
  right,
}: ScrollEdges): React.CSSProperties | undefined => {
  if (left || right) {
    const gradient = fade("right", left, right);
    return { maskImage: gradient, WebkitMaskImage: gradient };
  }
  if (!top && !bottom) return undefined;
  const gradient = fade("bottom", top, bottom);
  return { maskImage: gradient, WebkitMaskImage: gradient };
};
